// Staff portal domain types (staff-portal S1 — build brief §4).
// camelCase English fields per the workspace convention; snake_case ↔ camelCase
// mapping happens in the adapters only.

export type Team = 'menage' | 'receptionSalle' | 'cuisine';

export interface Employee {
  id: string;
  /** Payroll ID (« ID Paie » in the operator's register). */
  employeeId: string;
  lastName: string;
  firstName: string;
  email: string;
  /** E.164 (+336…) — the WhatsApp sender mapping key. */
  phone: string;
  /** Null until the operator completes the seed (missing from the register). */
  team: Team | null;
  isSenior: boolean;
  contractStartDate: string | null;
  /** Null = CDI (parent brief §5.3 — contract type is derived, not stored). */
  contractEndDate: string | null;
  /** Linked Supabase Auth account (magic-link auto-link, migration 0015); null until first sign-in. */
  authUserId: string | null;
  /** Optional short recognition name (chat, Planning/Pointage) — unique, case-insensitively, across all employees past and current. Null falls back to per-surface conventions (initials, "Prénom N."). */
  nickname: string | null;
}

/**
 * A « Hors Staff » person (ADR-0024): a recurring trusted non-employee — volunteer,
 * family, seasonal helper — who may clock in on the kiosk without a contract.
 * Deliberately NOT an `Employee`: it carries no contract, no login, no payroll id,
 * so nothing that accepts an `Employee` can be handed one by mistake.
 */
export interface TrustedPerson {
  id: string;
  firstName: string;
  lastName: string;
  nickname: string | null;
}

/** A « Hors Staff » person as the back-office sees them (adds PIN state and archival to `TrustedPerson`). */
export interface TrustedPersonRecord extends TrustedPerson {
  hasPin: boolean;
  /** ISO instant; null while active. An archived person cannot punch (ADR-0024 D5). */
  archivedAt: string | null;
}

/** A person on the desk list page (bos.* › RH › « Liste de la réception », ADR-0026):
 *  employees and Hors Staff alike (D10). `listed` is who « Qui êtes-vous ? » shows. */
export interface DeskListEntry {
  id: string;
  firstName: string;
  lastName: string;
  nickname: string | null;
  kind: 'employee' | 'trusted';
  /** No kiosk PIN yet: listed, but cannot identify until one is set. */
  hasPin: boolean;
  listed: boolean;
  archived: boolean;
}

/** A « Hors Staff » punch — presence only; never a timesheet line, so it has no hours. */
export interface TrustedEntry {
  id: number;
  personId: string;
  clockIn: string;
  clockOut: string | null;
  source: TimeEntrySource;
}

/** Seed/upsert input — only the fields the register export carries. */
export interface EmployeeUpsert {
  employeeId: string;
  lastName: string;
  firstName: string;
  email: string;
  phone: string;
}

/** 'wa' = bridge-mirrored WhatsApp; 'app' = native in-app write (M3); 'system' = board broadcast/alert (S4). */
export type MessageSource = 'wa' | 'app' | 'system';

export interface StaffMessage {
  id: number;
  /** Null for 'app' rows — WA redelivery dedup key only applies to 'wa'. */
  waMessageId: string | null;
  /** Null when the sender's phone matched no employee at ingest time ('wa' only — 'app' rows always have one). */
  employeeId: string | null;
  /** Wire sender (WhatsApp JID) as received — kept for unmapped senders + audit. 'app' rows carry a fixed marker. */
  senderRaw: string;
  body: string;
  hasMedia: boolean;
  sentAt: string;
  insertedAt: string;
  source: MessageSource;
}

/** A group message as normalized by the bridge before ingest. */
export interface IncomingGroupMessage {
  waMessageId: string;
  /** Raw WhatsApp participant JID (e.g. '33612345678@s.whatsapp.net'). */
  senderJid: string;
  /** Text body or media caption; empty string when media-only. */
  body: string;
  hasMedia: boolean;
  /** ISO timestamp. */
  sentAt: string;
}

export interface NewStaffMessage {
  waMessageId: string | null;
  employeeId: string | null;
  senderRaw: string;
  body: string;
  hasMedia: boolean;
  sentAt: string;
  source: MessageSource;
}

/** Marker sender for native app writes (senderRaw has no WA JID to record). */
export const APP_SENDER_MARKER = 'app';

/** Marker sender + display label for system rows (board broadcasts, S4). */
export const SYSTEM_SENDER_MARKER = 'system';
export const SYSTEM_SENDER_LABEL = 'Planning';

export type IngestResult = 'inserted' | 'duplicate' | 'skipped';

/** A staff app's Web Push subscription (M2 — installed-app push). */
export interface PushSubscriptionRecord {
  endpoint: string;
  userId: string;
  /** Subscription keys — kept for a future encrypted-payload upgrade; the payload-less v1 send only needs the endpoint. */
  p256dh: string;
  auth: string;
}

/** v1 text-only policy: media-only messages are logged as a placeholder. */
export const MEDIA_PLACEHOLDER = '[média]';

export interface StaffDigest {
  digestDate: string;
  bodyFr: string;
  messageCount: number;
}

export interface KnowledgeCandidate {
  id: string;
  digestDate: string;
  bodyFr: string;
  status: 'pending' | 'accepted' | 'dismissed';
}

/** What the nightly analyzer returns (S2). */
export interface AnalysisResult {
  /** French digest: decisions, pending items, unresolved questions. */
  digestFr: string;
  /** Anonymous, operational, reusable facts — often empty. */
  candidates: string[];
}

/** One channel message as the analyzer sees it. */
export interface AnalysisMessage {
  senderLabel: string;
  /** 'HH:MM' local time. */
  time: string;
  body: string;
}

// ── Time clock (S3) ──────────────────────────────────────────────────────────

export type TimeEntrySource = 'self' | 'operator';

export interface TimeEntry {
  id: number;
  employeeId: string;
  clockIn: string;
  /** Null = shift still open (or forgotten — operator corrects). */
  clockOut: string | null;
  source: TimeEntrySource;
  /** Operator correction trace (migration 0018 trigger) — null on kiosk rows. */
  updatedBy: string | null;
  updatedAt: string | null;
}

/**
 * The punch fields the read models actually consume. Typed structurally so a
 * caller holding a narrower row — the kiosk state carries no id and no
 * correction trace — can pass it without fabricating the rest.
 */
export type PunchLike = Pick<TimeEntry, 'employeeId' | 'clockIn' | 'clockOut'>;

/** Outcome of the staff_verify_pin RPC (Q-M). */
export type PinCheck = 'ok' | 'badPin' | 'noPin' | 'unknown';

/** What a kiosk punch produced (ClockService policy — see clockService.ts). */
export type PunchOutcome =
  | {
      kind: 'in';
      entry: TimeEntry;
      /** Set when a stale open shift (> MAX_OPEN_SHIFT_HOURS) was left for operator correction. */
      staleOpenEntryId: number | null;
    }
  | { kind: 'out'; entry: TimeEntry }
  | { kind: 'rejected'; reason: Exclude<PinCheck, 'ok'> };

/** What the kiosk needs to render: the roster, the « Hors Staff » people, and who is currently clocked in. */
export interface KioskState {
  employees: Employee[];
  /** « Hors Staff » people on the grid's second section — active (not archived) only. */
  trustedPersons: TrustedPerson[];
  openEntries: TimeEntry[];
  /** Every entry whose clock-in falls in "today" (Europe/Paris calendar day), any employee — the « Aujourd'hui » list. */
  todaysEntries: TimeEntry[];
}

// ── Day board — declared presence (minimal S4 slice for M4) ─────────────────

/** Tripartite day (0036): matin 06–12 · après-midi 12–18 · soir 18–23; absent = whole day. */
export type PresenceSlot = 'matin' | 'apresMidi' | 'soir' | 'absent';
/** What a channel MESSAGE may express — « toute la journée » is a linguistic unit; declareFromMessage expands it to the three slots. */
export type IntentSlot = PresenceSlot | 'journee';

/**
 * One person's declaration within a message (v3). slots and activityIds may
 * not both be empty.
 */
export interface IntentDeclaration {
  employeeId: string;
  /** Concrete days ('YYYY-MM-DD'), exclusions already applied, deduped, today-or-future, capped. */
  dates: string[];
  slots: IntentSlot[];
  /** board_activities ids, validated against the list given to the extractor. */
  activityIds: number[];
}

/**
 * M4/v2–v3: what a channel message resolves to — a closed schema. v2
 * (2026-07-19) generalised v1's single (date, slot): a message may span
 * MULTIPLE dates (« cette semaine sauf mardi » → the enumerated days) and may
 * name specific ACTIVITIES (« la permanence téléphone ») instead of — or
 * alongside — coarse slots. v3 (2026-07-19) generalises v2's single person:
 * a message may declare for SEVERAL people at once (« Dima repos lundi,
 * Sveta repos mercredi ») — one IntentDeclaration each.
 */
export type ParsedIntent =
  | { kind: 'presenceDeclaration'; declarations: IntentDeclaration[] }
  | { kind: 'none' };

/** Minimal roster shape the intent extractor needs to resolve names (first names are unique in this team). */
export interface RosterEntry {
  id: string;
  firstName: string;
  /** Chat recognition name (« Dima » for Dmytro) — the extractor must resolve diminutives too (v3). */
  nickname: string | null;
}

/** Minimal activity shape the intent extractor needs to resolve activity mentions (v2). */
export interface IntentActivity {
  id: number;
  label: string;
  /** 'HH:MM(:SS)' default window — shown to the model so it can tell matin from soir activities. */
  windowStart: string;
  windowEnd: string;
}

/** Local calendar day in Europe/Paris ('2026-07-15') — the intent extractor's date-resolution anchor. */
export function todayIsoParis(now: Date = new Date()): string {
  // en-CA formats as YYYY-MM-DD directly — no manual assembly needed.
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(now);
}

/**
 * Administrative start-of-day for payroll, in Europe/Paris hours.
 *
 * Restaurant shifts routinely cross midnight (18:00 → 03:00 is ONE working
 * day of 9 h, not two). French payroll practice handles this with an
 * administrative cutoff rather than by splitting the shift at midnight: a
 * shift is attributed in full to the business day its window began in.
 * 04:00 keeps 18:00→03:00 neatly inside Day 1, and is the SAME change of day
 * as the fiscal day (`pms_fiscal_day_of`): one day boundary for the business.
 */
export const BUSINESS_DAY_CUTOFF_HOUR = 4;

/**
 * The cutoff was 05:00 until the operator moved it to 04:00 (2026-10-13). The
 * change is FORWARD-ONLY: an instant before 14 Oct 2026 04:00 Paris keeps the
 * 05:00 cutoff, so a month already paid renders exactly as it was paid (Hard
 * Rule 5, history preservation). The day is derived when read, so without this
 * a past 04:xx clock-in would silently move to another day.
 */
export const BUSINESS_DAY_CUTOFF_HISTORY: ReadonlyArray<{ readonly until: string; readonly hour: number }> = [
  { until: '2026-10-14T02:00:00.000Z', hour: 5 }, // 14 Oct 2026 04:00 CEST
];

/** The cutoff hour in force at an instant. */
export function businessDayCutoffHourAt(instant: Date): number {
  const t = instant.getTime();
  return BUSINESS_DAY_CUTOFF_HISTORY.find((h) => t < Date.parse(h.until))?.hour ?? BUSINESS_DAY_CUTOFF_HOUR;
}

const parisWallClock = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Paris',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  hourCycle: 'h23',
});

/**
 * The BUSINESS day an instant belongs to ('2026-07-15') — Europe/Paris with
 * the cutoff in force at that instant (04:00 from 14 Oct 2026, 05:00 before).
 * Anything before the cutoff, Paris time, belongs to the previous day.
 *
 *   18:00 Day 1 → Day 1        03:00 Day 2 → Day 1 (the same night's shift)
 *   03:59 Day 2 → Day 1        04:00 Day 2 → Day 2 (cutoff is exclusive-below)
 *
 * Read on the Paris WALL CLOCK: the Paris date, minus one day when the Paris
 * hour is before the cutoff. (It used to subtract the cutoff from the absolute
 * instant, which is off by one day for an hour on each DST-change night — on
 * 25 Oct 2026, 04:30 CET came out as the 25th.)
 *
 * Use this for anything derived from a clock_in/clock_out timestamp. Use
 * todayIsoParis for a plain calendar day (the board's date-typed columns,
 * « today » in the UI) — those are NOT cutoff-shifted.
 */
export function businessDayOf(instant: Date): string {
  const parts = Object.fromEntries(parisWallClock.formatToParts(instant).map((p) => [p.type, p.value]));
  const day = `${parts.year}-${parts.month}-${parts.day}`;
  return Number(parts.hour) < businessDayCutoffHourAt(instant) ? addDaysUtc(day, -1) : day;
}

/** 'YYYY-MM-DD' ± whole days, via UTC so it is timezone- and DST-independent. */
function addDaysUtc(dayIso: string, delta: number): string {
  const d = new Date(`${dayIso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

/**
 * clock_in bounds for a `staff_time_entries` query covering business days
 * [fromDay, toDay]. `clock_in` is timestamptz, so it must be bounded by real
 * instants, not bare 'YYYY-MM-DD' strings.
 *
 * Deliberately a GENEROUS SUPERSET (a day either side, anchored in UTC): a
 * business day runs to 04:00 Paris the following morning, so exact bounds
 * would need a DST-aware Paris-instant constructor at every call site. The
 * precise filtering already lives in one pure, tested place (computeTimesheet
 * filters by businessDayOf; computeDayWorkedHours buckets and the caller reads
 * only the keys it wants), so over-fetching a couple of days is free and
 * removes a whole class of boundary bug.
 *
 * Anchoring in UTC also fixes the old `new Date(\`${day}T00:00:00\`)` pattern,
 * which used the DEVICE's midnight — on a non-Paris device that silently
 * excluded real entries from the window before any bucketing could see them.
 */
export function businessDayFetchRange(
  fromDay: string,
  toDay: string,
): { fromIso: string; toIso: string } {
  return {
    fromIso: `${addDaysUtc(fromDay, -1)}T00:00:00.000Z`,
    toIso: `${addDaysUtc(toDay, 2)}T00:00:00.000Z`,
  };
}

/** Europe/Paris UTC offset (minutes, e.g. 60 CET / 120 CEST) at a given instant. */
function parisOffsetMinutes(instant: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Paris',
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return Math.round((asUtc - instant.getTime()) / 60_000);
}

/**
 * Exact UTC instants for Europe/Paris calendar-day midnight-to-midnight
 * (DST-aware — each edge is resolved from the Paris offset in effect at that
 * edge, so it stays correct across the March/October transitions). Unlike
 * `businessDayFetchRange`, this is NOT a superset: use it only where the
 * caller needs the precise instant boundary itself (e.g. a service-role edge
 * function with no Paris "local" clock of its own), not for a range that
 * will be bucketed afterwards.
 */
export function parisDayIsoRange(dayIso: string): { fromIso: string; toIso: string } {
  const parisMidnightIso = (day: string): string => {
    const guessMs = Date.parse(`${day}T00:00:00Z`);
    return new Date(guessMs - parisOffsetMinutes(new Date(guessMs)) * 60_000).toISOString();
  };
  return { fromIso: parisMidnightIso(dayIso), toIso: parisMidnightIso(addDaysUtc(dayIso, 1)) };
}

/**
 * Currently under contract: no end date, or an end date not yet passed.
 * Same semantics as the staff_current_employees view (migration 0031) and
 * the back-office « en poste » filter — departed employees stay in the
 * roster (message-name mapping, payroll history) but drop out of the kiosk
 * grid, the Présence board, and the intent extractor's name resolution.
 */
export function isCurrentEmployee(e: Employee, todayIso: string = todayIsoParis()): boolean {
  return e.contractEndDate === null || e.contractEndDate >= todayIso;
}

/**
 * Display name for a message sender: employee first name + last initial, or
 * a readable fallback (E.164 phone, else the raw JID). Shared by the staff
 * app feed and the S2 analyzer input.
 */
export function senderLabel(msg: StaffMessage, employeesById: Map<string, Employee>): string {
  if (msg.source === 'system') return SYSTEM_SENDER_LABEL;
  const employee = msg.employeeId ? employeesById.get(msg.employeeId) : undefined;
  if (employee) {
    if (employee.nickname) return employee.nickname;
    const initial = employee.lastName.trim().charAt(0);
    return initial ? `${employee.firstName} ${initial}.` : employee.firstName;
  }
  const phone = waJidToPhone(msg.senderRaw);
  return phone ?? msg.senderRaw;
}

/**
 * WhatsApp participant JID → E.164 phone ('+33612345678'), or null when the
 * JID is not phone-addressed (e.g. @lid linked-device ids) — those ingest
 * unmapped with the raw JID preserved in senderRaw.
 */
export function waJidToPhone(jid: string): string | null {
  const at = jid.indexOf('@');
  if (at <= 0 || !jid.slice(at + 1).startsWith('s.whatsapp.net')) return null;
  // Strip an optional device suffix ('33612345678:12').
  const user = jid.slice(0, at).split(':')[0] ?? '';
  if (!/^\d{6,15}$/.test(user)) return null;
  return `+${user}`;
}

/**
 * ISO instant → Europe/Paris 'HH:MM'.
 *
 * Explicit zone, never the runtime's: a clock instant is `timestamptz`, and the
 * third of the day it falls in is Paris WALL-CLOCK time, which a CF worker or a
 * CI box has no reason to share. Lives here rather than in an app because both
 * the reception board and the server-side corpus job need the same answer —
 * two implementations of one time rule is how they drift apart.
 */
const PARIS_HM = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Paris',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export function parisHM(iso: string): string {
  return PARIS_HM.format(new Date(iso));
}
